/**
 * EventsDisplay Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Comprehensive events management and display system for the Irish Funds member portal.
 * This component provides sophisticated event browsing capabilities with enterprise features:
 * 
 * CORE FEATURES:
 * - Advanced event filtering with multi-dimensional criteria
 * - Real-time search across multiple event fields
 * - Dynamic image loading with API integration and fallback handling
 * - Responsive pagination with smart page size management
 * - Event type categorization (upcoming, past, all events)
 * - Professional modal system for detailed event viewing
 * - Collateral indicator system for videos, audio, and documents
 * - Mobile-responsive design with adaptive filter controls
 * 
 * FILTERING SYSTEM:
 * - Multi-criteria filtering: month, year, event type, topics, category
 * - Smart dropdown system with cascading filter dependencies
 * - Real-time filter state management with instant results
 * - Visual filter badges with individual removal capabilities
 * - Combined search and filter functionality
 * 
 * IMAGE MANAGEMENT:
 * - Intelligent image loading with multiple source priority handling
 * - API-based image retrieval with blob URL creation
 * - Fallback system for failed image loads
 * - Memory management with blob URL cleanup
 * - Support for HTTPS URLs and local asset paths
 * 
 * EVENT NAVIGATION:
 * - Modal-based event detail viewing with fullscreen support
 * - Previous/next navigation within filtered result sets
 * - Event registration link integration
 * - Collateral access (video, audio, PDF documents)
 * - Responsive layout adaptation for mobile devices
 * 
 * TECHNICAL ARCHITECTURE:
 * - React Hooks for complex state management
 * - useEffect for lifecycle and dependency handling
 * - Event-driven modal system with navigation controls
 * - Professional error handling and graceful degradation
 * - Memory-efficient blob URL management
 * - Production-ready with comprehensive documentation
 * 
 * @param {Object} props - Component props
 * @param {Array} props.eventsData - Array of event objects with complete event information
 * @param {Array} props.eventsData[].ID - Unique event identifier
 * @param {string} props.eventsData[].Title - Event title
 * @param {string} props.eventsData[].EventDescription_x002f_Narrative - Event description
 * @param {string} props.eventsData[].EventDate - Event date (ISO format)
 * @param {string} props.eventsData[].EventLocation - Event location
 * @param {string} props.eventsData[].EventCategory - Event category classification
 * @param {string} props.eventsData[].InPerson_x002f_Virtual - Event format (In Person/Virtual)
 * @param {Array} props.eventsData[].Topics - Array of topic tags
 * @param {string} props.eventsData[].ImageLink - Event image URL or API reference
 * @param {string} props.eventsData[].VideoCollateral - Video content reference
 * @param {string} props.eventsData[].AudioCollateral - Audio content reference
 * @param {string} props.eventsData[].FileCollateral - PDF document reference
 * @param {string} props.eventsData[].RegistrationLink - Registration URL
 * @param {Array} props.filterTypes - Filter configuration array
 * @param {string} props.filterTypes[].id - Filter identifier
 * @param {string} props.filterTypes[].label - Filter display label
 * @param {Array} props.filterTypes[].options - Available filter options
 * @param {boolean} props.filterTypes[].isMulti - Multi-selection capability flag
 * @returns {JSX.Element} Complete events browsing interface with filtering and modal system
 */
import React, { useState, useEffect } from "react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Modal from "../../modals/NewsModal";
import EventsModal from "../../modals/EventsModal";
import apiHelper from "../../utils/apiHelper";
import "./EventsDisplay.css";

/**
 * EventsDisplay Component
 * 
 * Main component for displaying and managing events with comprehensive filtering,
 * search, and modal navigation capabilities.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.eventsData - Complete events dataset
 * @param {Array} props.filterTypes - Filter configuration
 * @returns {JSX.Element} Events display interface
 */
function EventsDisplay({ eventsData, filterTypes }) {
  const [isOpen, setIsOpen] = useState({
    month: false,
    year: false,
    event_type: false,
    topics: false,
    category: false,
  });
  const [filtersSelected, setFiltersSelected] = useState({
    month: "",
    year: "",
    event_type: "",
    topics: [],
    category: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [eventTypeSelected, setEventTypeSelected] = useState({
    type: "upcoming",
    filteredEvents: [],
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCollateral, setSelectedCollateral] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  
  // Image loading state - similar to NewsModal pattern
  const [eventImages, setEventImages] = useState({});

  /**
   * Advanced event image loading system with multiple source handling
   * 
   * Implements sophisticated image loading logic with fallback mechanisms:
   * - Checks for existing cached images to avoid redundant API calls
   * - Handles blob URLs, HTTPS URLs, and local asset paths
   * - Integrates with backend Graph API for secure image retrieval
   * - Creates blob URLs for API-retrieved images with proper memory management
   * - Provides consistent fallback to default images on failure
   * 
   * @param {Object} event - Event object containing image information
   * @param {string} event.ID - Unique event identifier for caching
   * @param {string} event.ImageLink - Image URL or API reference
   * @returns {Promise<void>} Resolves when image loading is complete
   */
  const loadEventImage = async (event) => {
    if (!event.ImageLink) {
      return;
    }
    
    if (eventImages[event.ID]) {
      return; // Already loaded
    }

    // Check if it's already a blob URL
    if (event.ImageLink.startsWith('blob:')) {
      setEventImages(prev => ({ ...prev, [event.ID]: event.ImageLink }));
      return;
    }

    // Check if it's an HTTPS URL
    if (event.ImageLink.includes('https://')) {
      setEventImages(prev => ({ ...prev, [event.ID]: event.ImageLink }));
      return;
    }

    // Check if it's a local asset path
    if (event.ImageLink.includes('assets/img/') || event.ImageLink.startsWith('/images/')) {
      setEventImages(prev => ({ ...prev, [event.ID]: event.ImageLink }));
      return;
    }

    // Otherwise, try to load via API like NewsModal does
    try {
      const authToken = await apiHelper.getAuthToken();
      const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${event.ImageLink}`;
      
      const response = await fetch(imageUrl, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setEventImages(prev => ({ ...prev, [event.ID]: blobUrl }));
      } else {
        // Fallback to consistent default image
        setEventImages(prev => ({ ...prev, [event.ID]: "/images/news-reading.jpg" }));
      }
    } catch (error) {
      // Fallback to consistent default image
      setEventImages(prev => ({ ...prev, [event.ID]: "/images/news-reading.jpg" }));
    }
  };

  useEffect(() => {
    if (eventsData.length > 0) {
      handleFilterEventType(eventTypeSelected.type);
      
      // Load images for all events
      eventsData.forEach(event => {
        if (event.ImageLink) {
          loadEventImage(event);
        }
      });
    }
  }, [eventsData, eventTypeSelected.type]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(eventImages).forEach(imageUrl => {
        if (imageUrl && imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(imageUrl);
        }
      });
    };
  }, [eventImages]);

  useEffect(() => {
    let filteredEvents = [...eventsData];
    const now = new Date();
    const currentMonth = now.getMonth() + 1;

    if (eventTypeSelected.type === "upcoming") {
      filteredEvents = filteredEvents.filter((event) => {
        if (event.EventDate) {
          const eventDate = new Date(event.EventDate);
          return eventDate > now;
        } else if (event.EventMonth) {
          const monthNum = parseInt(event.EventMonth);
          return (
            !isNaN(monthNum) &&
            monthNum >= 1 &&
            monthNum < 13 &&
            monthNum >= currentMonth
          );
        }
        return false;
      });
    } else if (eventTypeSelected.type === "past") {
      filteredEvents = eventsData.filter((event) => {
        if (event.EventDate) {
          const eventDate = new Date(event.EventDate);
          return eventDate < now;
        }
        if (event.EventMonth) {
          const monthNum = parseInt(event.EventMonth);
          return (
            !isNaN(monthNum) &&
            monthNum >= 1 &&
            monthNum < 13 &&
            monthNum < currentMonth
          );
        }
        return false;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredEvents = filteredEvents.filter((item) => {
        const searchableFields = [
          item.Title,
          item.EventDescription_x002f_Narrative,
          item.EventLocation,
          item.EventCategory,
          item.InPerson_x002f_Virtual,
          item.Speakers,
          item.Agenda,
          ...(item.Topics || [])
        ];
        
        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // filter data
    filteredEvents = filteredEvents.filter((item) => {
      const matchCategory =
        !filtersSelected.category ||
        item.EventCategory === filtersSelected.category;
      const matchEventType =
        !filtersSelected.event_type ||
        item.InPerson_x002f_Virtual === filtersSelected.event_type;
      const matchTopics =
        filtersSelected.topics.length === 0 ||
        (item.Topics &&
          filtersSelected.topics.every((topic) => item.Topics.includes(topic)));

      return matchCategory && matchEventType && matchTopics;
    });

    // year
    if (filtersSelected.year) {
      filteredEvents = filteredEvents.filter((event) => {
        const dateParts = event.EventDate.split("-");
        const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        let m = "";

        if (!isNaN(date)) {
          m = date.getUTCFullYear();
        }

        return m && m === filtersSelected.year;
      });
    }

    // month
    if (filtersSelected.month) {
      filteredEvents = filteredEvents.filter((event) => {
        const dateParts = event.EventDate.split("-");
        const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
        let formatter = new Intl.DateTimeFormat("en", { month: "long" });
        let m = "";

        if (!isNaN(date)) {
          m = formatter.format(new Date(2025, date.getMonth(), 1));
        } else if (event.EventMonth) {
          const monthNum = parseInt(event.EventMonth);
          if (!isNaN(monthNum) && monthNum >= 1 && monthNum < 13) {
            m = formatter.format(new Date(2025, monthNum - 1, 1));
          }
        }

        return m && m === filtersSelected.month;
      });
    }

    setFilteredEvents(filteredEvents);
    
    // Load images for newly filtered events
    filteredEvents.forEach(event => {
      if (event.ImageLink && !eventImages[event.ID]) {
        loadEventImage(event);
      }
    });
  }, [eventTypeSelected, filtersSelected, eventsData, searchQuery]);

  /**
   * Generate intelligent pagination array with ellipsis handling
   * 
   * Creates a smart pagination system that adapts to different page counts:
   * - Shows all pages when total pages ≤ 4
   * - Uses ellipsis (...) to condense large page ranges
   * - Maintains context around current page for easy navigation
   * - Optimizes display for both desktop and mobile interfaces
   * 
   * @param {number} currentPage - Currently active page number
   * @param {number} totalPages - Total number of pages available
   * @returns {Array} Array of page numbers and ellipsis strings for rendering
   */
  const getPagination = (currentPage, totalPages) => {
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage, "...", totalPages);
      }
    }

    return pages;
  };

  const toggleDropdown = (id) =>
    setIsOpen((prev) => ({
      month: false,
      year: false,
      event_type: false,
      topics: false,
      category: false,
      [id]: !prev[id],
    }));

  const handleSelectOption = (id, option) => {
    setIsOpen((prev) => ({ ...prev, [id]: id === "topics" ? true : false }));

    setFiltersSelected((prev) => {
      if (id === "topics") {
        const alreadySelected = prev.topics.includes(option);
        return {
          ...prev,
          topics: alreadySelected
            ? prev.topics.filter((t) => t !== option)
            : [...prev.topics, option],
        };
      } else {
        return {
          ...prev,
          [id]: option,
        };
      }
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFiltersSelected({
      month: "",
      year: "",
      event_type: "",
      topics: [],
      category: "",
    });
    setSearchQuery("");

    handleFilterEventType(eventTypeSelected.type);
  };

  const handleFilterEventType = (key) => {
    setEventTypeSelected({ type: key });
    setCurrentPage(1);
  };

  /**
   * Professional modal navigation system for event browsing
   * 
   * Provides comprehensive modal-based event viewing with navigation controls:
   * - Event selection and modal opening
   * - Previous/next navigation within filtered results
   * - Modal state management and cleanup
   * - Navigation boundary detection and handling
   */

  /**
   * Handle event selection and modal opening
   * 
   * @param {Object} event - Event object to display in modal
   */
  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  /**
   * Handle modal close and state cleanup
   */
  const handleModalClose = () => {
    setSelectedEvent(null);
  };

  /**
   * Navigate to next event in filtered results
   * 
   * Moves to the next event in the current filtered dataset,
   * respecting the current filter and search state.
   */
  const handleNextEvent = () => {
    if (!selectedEvent) return;
    
    const currentIndex = filteredEvents.findIndex(event => event.ID === selectedEvent.ID);
    if (currentIndex < filteredEvents.length - 1) {
      setSelectedEvent(filteredEvents[currentIndex + 1]);
    }
  };

  /**
   * Navigate to previous event in filtered results
   * 
   * Moves to the previous event in the current filtered dataset,
   * respecting the current filter and search state.
   */
  const handlePreviousEvent = () => {
    if (!selectedEvent) return;
    
    const currentIndex = filteredEvents.findIndex(event => event.ID === selectedEvent.ID);
    if (currentIndex > 0) {
      setSelectedEvent(filteredEvents[currentIndex - 1]);
    }
  };

  /**
   * Get next event for navigation button state
   * 
   * @returns {Object|null} Next event object or null if at end
   */
  const getNextEvent = () => {
    if (!selectedEvent) return null;
    
    const currentIndex = filteredEvents.findIndex(event => event.ID === selectedEvent.ID);
    return currentIndex < filteredEvents.length - 1 ? filteredEvents[currentIndex + 1] : null;
  };

  /**
   * Get previous event for navigation button state
   * 
   * @returns {Object|null} Previous event object or null if at beginning
   */
  const getPreviousEvent = () => {
    if (!selectedEvent) return null;
    
    const currentIndex = filteredEvents.findIndex(event => event.ID === selectedEvent.ID);
    return currentIndex > 0 ? filteredEvents[currentIndex - 1] : null;
  };



  const eventTypeList = [
    { key: "upcoming", label: "Upcoming Events" },
    { key: "past", label: "Past Events" },
    { key: "all", label: "All Events" },
  ];
  const itemsPerPage = 6;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEvents = filteredEvents
    ? filteredEvents.slice(startIndex, startIndex + itemsPerPage)
    : [];

  return (
    <div>
      <div className="event-tabs">
        {eventTypeList.map((item, index) => (
          <div
            key={index}
            onClick={() => handleFilterEventType(item.key)}
            className="event-tab"
          >
            <div
              className="event-tab-green"
              style={{
                backgroundColor:
                  eventTypeSelected.type === item.key ? "#377E22" : "#EAEFEA",
              }}
            />
            <div
              className="event-tab-text"
              style={{
                backgroundColor:
                  eventTypeSelected.type === item.key ? "#343F52" : "white",
                border:
                  eventTypeSelected.type === item.key
                    ? ""
                    : "1px solid #EAEFEA",
                color:
                  eventTypeSelected.type === item.key ? "white" : "#7A7A7A",
              }}
            >
              <span>{item.label}</span>
              {eventTypeSelected.type === item.key ? (
                <img
                  src={"/assets/img/events/arrow-down-circle-white.svg"}
                  alt="down arrow"
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        ))}
      </div>

      <input
        type="checkbox"
        id="mobile-filter-toggle"
        className="mobile-filter-checkbox"
        hidden
      />
      <label
        htmlFor="mobile-filter-toggle"
        className="mobile-filter-label font-14"
      >
        <span className="mobile-filter-text">Filter by:</span>
        <img
          src="/assets/img/events/arrow-down-circle-green.svg"
          className="mobile-filter-icon"
          alt="Toggle Filters"
        />
      </label>

      <div className="mobile-filter-wrapper">
        <div className="select-filters-dropdown-container">
          <div className="font-14 filter-by-label">Filter by:</div>

          {filterTypes.map(({ id, label, options, isMulti }) => {
            if (options.length === 0) return null;
            return (
              <div key={id} className="select-filters-dropdown">
                <div
                  className="dropdown-filter font-14"
                  onClick={() => toggleDropdown(id)}
                  style={{
                    backgroundColor: isOpen[id] ? "#377E22" : "#EAEFEA",
                    color: isOpen[id] ? "white" : "#262B32",
                    justifySelf: "center",
                  }}
                >
                  {label}
                  <FontAwesomeIcon
                    className="dropdown-filter-icon"
                    icon={faChevronDown}
                    style={{
                      transform: isOpen[id]
                        ? "translateY(-50%) rotate(180deg)"
                        : "translateY(-50%) rotate(0deg)",
                    }}
                  />
                </div>

                {isOpen[id] && (
                  <div
                    className="dropdown-open"
                    style={{
                      flexDirection: isMulti ? "row" : "column",
                      flexWrap: isMulti ? "wrap" : "nowrap",
                      width: isMulti ? 720 : 250,
                      gap: isMulti ? 10 : 0,
                    }}
                  >
                    {options.map((option) => (
                      <div
                        key={option}
                        onClick={() => handleSelectOption(id, option)}
                        className="font-14 dropdown-open-options"
                        style={{
                          width: isMulti ? "calc(33.33% - 10px)" : "100%",
                          padding: isMulti ? "0px 5px" : "3px 10px",
                        }}
                      >
                        {id === "topics" && (
                          <input
                            type="checkbox"
                            checked={filtersSelected.topics.includes(option)}
                            className="dropdown-checkbox"
                          />
                        )}
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="filters-selected">
        {Object.entries(filtersSelected).map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((item, index) => (
              <div key={`${key}-${index}`} className="filterBadge font-14">
                <span>{item}</span>
                <img
                  src="/assets/img/representation/close.svg"
                  onClick={() =>
                    setFiltersSelected((prev) => ({
                      ...prev,
                      [key]: prev[key].filter((i) => i !== item),
                    }))
                  }
                  alt="close button icon"
                  className="filters-close-img"
                />
              </div>
            ));
          } else if (value) {
            return (
              <div key={key} className="filterBadge font-14">
                <span>{value}</span>
                <img
                  src="/assets/img/representation/close.svg"
                  onClick={() =>
                    setFiltersSelected((prev) => ({ ...prev, [key]: "" }))
                  }
                  className="close-img"
                  alt="close button icon"
                />
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Search Bar */}
      <div className="events-search-container">
        <div className="search-and-clear-row">
          <div className="events-search-bar">
            <i className="fas fa-search events-search-icon"></i>
                          <input
                type="text"
                placeholder="Search events by title, description, location, speakers..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                className="events-search-input"
              />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                className="search-clear-btn"
                title="Clear search"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {(Object.entries(filtersSelected).some(([key, val]) =>
            Array.isArray(val) ? val.length > 0 : val !== ""
          ) || searchQuery.trim()) && (
            <button
              onClick={handleClearFilters}
              className="clear-all-btn font-14"
            >
              Clear All
            </button>
          )}
        </div>
        {searchQuery && (
          <div className="search-results-info">
            Showing {filteredEvents.length} result{filteredEvents.length !== 1 ? 's' : ''} for "{searchQuery}"
          </div>
        )}
      </div>

      <div className="events-container">
        {currentEvents.length > 0 ? (
          currentEvents.map((event, i) => (
            <div key={event.ID} className="bg-primary event-item">
              <div>
                <div className="event-item-image-container clickable-image" onClick={() => handleEventClick(event)}>
                  <img
                    src={(() => {
                      const loadedImage = eventImages[event.ID];
                      const originalImage = event.ImageLink;
                      
                      // Priority order:
                      // 1. Use loaded image (blob URL from API)
                      if (loadedImage) {
                        return loadedImage;
                      }
                      
                      // 2. Use original HTTPS URL if available
                      if (originalImage && originalImage.includes('https://')) {
                        return originalImage;
                      }
                      
                      // 3. Fallback to default
                      return "/images/news-reading.jpg";
                    })()}
                    className="event-item-image"
                    alt="event header"
                    onError={(e) => {
                      // Fallback to default image if loading fails
                      if (e.target.src !== "/images/news-reading.jpg") {
                        e.target.src = "/images/news-reading.jpg";
                      }
                    }}
                    onLoad={(e) => {
                      // Image loaded successfully
                    }}
                  />
                  
                  {/* Collateral indicators - positioned on the image itself */}
                  {(event.VideoCollateral || event.AudioCollateral || event.FileCollateral) && (
                    <div className="event-collateral-indicators">
                      {event.VideoCollateral && (
                        <Tippy content="Video Available" placement="top">
                          <div className="collateral-icon video-icon">
                            <i className="fas fa-video"></i>
                          </div>
                        </Tippy>
                      )}
                      {event.AudioCollateral && (
                        <Tippy content="Audio/Podcast Available" placement="top">
                          <div className="collateral-icon audio-icon-events">
                            <i className="fas fa-microphone"></i>
                          </div>
                        </Tippy>
                      )}
                      {event.FileCollateral && (
                        <Tippy content="PDF Document Available" placement="top">
                          <div className="collateral-icon file-icon">
                            <i className="fas fa-file-pdf"></i>
                          </div>
                        </Tippy>
                      )}
                    </div>
                  )}
                </div>
                <div className="event-item-image-badge-container">
                  {event.EventCategory ? (
                    <div className="event-item-image-badge">
                      <span className="event-item-badge-text font-14">
                        {event.EventCategory}
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}
                  {event.InPerson_x002f_Virtual ? (
                    <div className="event-item-image-badge-type-event">
                      <span className="event-item-badge-text-event-type font-14">
                        {event.InPerson_x002f_Virtual}
                      </span>
                    </div>
                  ) : (
                    <></>
                  )}

                </div>
              </div>

              <div className="event-item-info">
                <div>
                  <img src={"/assets/img/events/date.svg"} alt="date icon" />
                  {event.EventDate ? (
                    <p>
                      {new Intl.DateTimeFormat("en-GB", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        timeZone: "UTC",
                      }).format(new Date(event.EventDate))}
                    </p>
                  ) : event.EventMonth ? (
                    <p>
                      {new Date(2025, event.EventMonth - 1).toLocaleString(
                        "default",
                        { month: "long" }
                      )}
                    </p>
                  ) : (
                    <p>TBC</p>
                  )}
                </div>
                <div>
                  <img src={"/assets/img/events/time.svg"} alt="time icon" />
                  {event.StartTime && event.EndTime ? (
                    event.StartTime === "TBC" && event.EndTime === "TBC" ? (
                      <p>TBC</p>
                    ) : (
                      <p>
                        {event.StartTime === "TBC"
                          ? "TBC"
                          : `${(() => {
                              const [h, m] = event.StartTime.split(":");
                              const hour = parseInt(h) % 12 || 12;
                              const ampm = parseInt(h) >= 12 ? "pm" : "am";
                              return `${hour}:${m} ${ampm}`;
                            })()}`}
                        {" - "}
                        {event.EndTime === "TBC"
                          ? "TBC"
                          : `${(() => {
                              const [h, m] = event.EndTime.split(":");
                              const hour = parseInt(h) % 12 || 12;
                              const ampm = parseInt(h) >= 12 ? "pm" : "am";
                              return `${hour}:${m} ${ampm}`;
                            })()}`}
                      </p>
                    )
                  ) : (
                    <p>TBC</p>
                  )}
                </div>
                <div>
                  <img
                    src={"/assets/img/events/location.svg"}
                    alt="location icon"
                  />
                  <p>{event.EventLocation ? event.EventLocation : "TBC"}</p>
                </div>
                {event.AgendaLink && (
                  <div>
                    <img
                      src={"/assets/img/events/agenda.svg"}
                      alt="agenda icon"
                    />
                    <a className="agenda-link" href={event.AgendaLink}>
                      Agenda
                    </a>
                  </div>
                )}
              </div>

              <div className="event-item-title mb-3 desktop-only">
                {event.EventDescription_x002f_Narrative ? (
                  <Tippy
                    content={event.EventDescription_x002f_Narrative}
                    placement="bottom"
                  >
                    <p className="title clickable-title" onClick={() => handleEventClick(event)}>
                      {event.Title}
                    </p>
                  </Tippy>
                ) : (
                  <p className="title clickable-title" onClick={() => handleEventClick(event)}>
                    {event.Title}
                  </p>
                )}
              </div>

              <div className="event-item-desc mb-3 mobile-only-events">
                {event.EventDescription_x002f_Narrative ? (
                  <Tippy
                    content={event.EventDescription_x002f_Narrative}
                    placement="bottom"
                  >
                    <p className="title clickable-title" onClick={() => handleEventClick(event)}>
                      {event.Title}
                    </p>
                  </Tippy>
                ) : (
                  <p className="title clickable-title" onClick={() => handleEventClick(event)}>
                    {event.Title}
                  </p>
                )}
                {event.EventDescription_x002f_Narrative ? (
                  <p className="text">
                    {event.EventDescription_x002f_Narrative}
                  </p>
                ) : (
                  <></>
                )}
              </div>

              {event.Topics && event.Topics.length > 0 ? (
                <div className="event-tags">
                  {event.Topics.map((item, index) => (
                    <div key={index}>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <></>
              )}

              <div className="event-buttons">
                {event.RegistrationLink ? (
                  <div
                    onClick={() =>
                      window.open(event.RegistrationLink, "_blank")
                    }
                    className="register-btn"
                  >
                    Register
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="container no-events">
            <h4>No Events Found</h4>
          </div>
        )}
      </div>

      <div className="pagination-controls">
        <span
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          className="prev-btn-pagination font-14"
        >
          Prev
        </span>

        {getPagination(
          currentPage,
          Math.ceil(
            (filteredEvents ? filteredEvents.length : 0) / itemsPerPage
          )
        ).map((page, index) =>
          typeof page === "number" ? (
            <button
              key={index}
              onClick={() => setCurrentPage(page)}
              className="page-number-btn"
              style={{
                backgroundColor: currentPage === page ? "#EAEFEA" : "white",
              }}
            >
              {page}
            </button>
          ) : (
            <span key={index} className="page-ellipse font-14">
              {page}
            </span>
          )
        )}

        <span
          onClick={() =>
            setCurrentPage((prev) =>
              Math.min(
                prev + 1,
                Math.ceil(filteredEvents.length / itemsPerPage)
              )
            )
          }
          className="next-btn-pagination font-14"
        >
          Next
        </span>
      </div>

      {selectedCollateral ? (
        <Modal
          showImage={false}
          data={{
            title: selectedCollateral.Title,
            externalUrls: true,
            podcast: selectedCollateral.AudioCollateral,
            video: selectedCollateral.VideoCollateral,
            file: selectedCollateral.FileCollateral,
            description: "",
          }}
          show={selectedCollateral != null}
          onClose={() => {
            setSelectedCollateral(null);
          }}
        />
      ) : null}

      {selectedEvent && (
        <EventsModal
          data={selectedEvent}
          show={selectedEvent !== null}
          onClose={handleModalClose}
          allowFullscreen={true}
          onNext={handleNextEvent}
          onPrevious={handlePreviousEvent}
          hasNext={getNextEvent() !== null}
          hasPrevious={getPreviousEvent() !== null}
          nextEvent={getNextEvent()}
          previousEvent={getPreviousEvent()}
        />
      )}
    </div>
  );
}

export default EventsDisplay;